import { useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { Link, Navigate, useParams } from 'react-router-dom';
import RackNav from '@/components/rack/RackNav';
import RackFooter from '@/components/rack/RackFooter';
import { channels, ACCENT_HEX } from '@/data/channels';
import { caseSets, STATUS_LABEL, type Status } from '@/data/cases';

/**
 * 案例页 —— 一个通道一页，三页共用这一套版式。
 *
 * 首页讲的是「怎么做」（方法与仪器），这里讲的是「能给客户什么」。三层从宽到窄：
 * 能交付什么形态 → 能进哪些业务领域 → 真做过什么。业务领域那层每条都带验收标准，
 * 因为对客户来说，说得出拿什么验收才算真做过——只说能做，谁都会说。
 *
 * 版式沿用机架的 hairline datasheet，不引入卡片网格：三页和首页的三个通道段落
 * 必须看起来是同一台机器上的抽屉。
 */
export default function CaseIndex() {
  const { channel: slug } = useParams();
  const ch = channels.find((c) => c.id === slug);
  const set = slug ? caseSets[slug] : undefined;

  // 从首页点进来时浏览器会保留滚动位置，落到半截页面上
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [slug]);

  if (!ch || !set) return <Navigate to="/" replace />;

  const hex = ACCENT_HEX[ch.accent];
  const idx = channels.indexOf(ch);
  const next = channels[(idx + 1) % channels.length];

  return (
    <div className="min-h-screen bg-rack-ground font-hei text-rack-bone antialiased">
      <Helmet>
        <title>{`${ch.name}案例 · 能交付什么｜阳仔AI`}</title>
        <meta
          name="description"
          content={`${ch.name}方向的交付形态与业务领域：${set.forms
            .map((f) => f.label)
            .join('、')}。${set.domains.map((d) => d.name).join('、')}等场景的做法、交付物与验收标准。`}
        />
        <link rel="canonical" href={`https://minddock.ai/cases/${ch.id}`} />
      </Helmet>

      <RackNav />

      <main className="pt-24 sm:pt-28">
        <div className="mx-auto max-w-[1180px] px-6 sm:px-10">
          {/* 面包屑：案例页是首页那个通道的抽屉，得让人知道自己在哪一层 */}
          <nav className="flex items-center gap-2 font-readout text-[10px] uppercase tracking-[0.18em] text-rack-bone/25">
            <Link to="/" className="transition-colors hover:text-rack-bone/60">
              INDEX
            </Link>
            <span aria-hidden="true">/</span>
            <Link to={`/#${ch.id}`} className="transition-colors hover:text-rack-bone/60">
              {ch.no}
            </Link>
            <span aria-hidden="true">/</span>
            <span style={{ color: hex }}>CASES</span>
          </nav>

          <header className="mt-8 border-b border-rack-bone/12 pb-12">
            <div className="flex flex-wrap items-baseline gap-x-5 gap-y-2">
              <span className="font-readout text-[11px] tracking-[0.22em]" style={{ color: hex }}>
                {ch.no}
              </span>
              <h1 className="font-songti text-[clamp(1.7rem,4.6vw,2.5rem)] font-semibold tracking-[0.14em] text-rack-bone">
                {ch.name}
              </h1>
              <span className="font-readout text-[10px] uppercase tracking-[0.3em] text-rack-bone/22">
                {ch.en} · CASES
              </span>
            </div>

            <p className="mt-7 max-w-[30ch] font-songti text-[clamp(1.15rem,2.9vw,1.6rem)] font-semibold leading-[1.5] tracking-[0.08em] text-rack-bone/92">
              {ch.thesis}
            </p>
            <p className="mt-5 max-w-[62ch] text-[13.5px] leading-[1.95] text-rack-bone/50">
              {ch.lede}
            </p>
          </header>

          {/* ── 能交付什么形态 ── */}
          <Block no="01" title="能交付什么形态" hex={hex} note="同一套底层能力，按客户的场景选落地形态。">
            <div className="border-t border-rack-bone/12">
              {set.forms.map((f) => (
                <div
                  key={f.label}
                  className="grid items-baseline gap-x-7 gap-y-1.5 border-b border-rack-bone/[0.07] py-4 md:grid-cols-[168px_88px_1fr]"
                >
                  <span className="text-[13.5px] font-medium text-rack-bone/90">{f.label}</span>
                  <span className="font-readout text-[9.5px] uppercase tracking-[0.14em] text-rack-bone/26">
                    {f.en}
                  </span>
                  <span className="text-[13px] leading-[1.9] text-rack-bone/50">{f.deliver}</span>
                </div>
              ))}
            </div>
          </Block>

          {/* ── 业务领域：这一层是说服力的主要来源 ── */}
          <Block
            no="02"
            title="能进哪些业务领域"
            hex={hex}
            note="每条写清客户真正头疼的事、交付物，以及拿什么判定做成了。"
          >
            <div className="border-t border-rack-bone/12">
              {set.domains.map((d, i) => (
                <article
                  key={d.name}
                  className="grid gap-x-8 gap-y-4 border-b border-rack-bone/[0.07] py-8 lg:grid-cols-[188px_1fr]"
                >
                  <div className="flex items-start gap-3">
                    <span className="mt-[9px] h-px w-3 shrink-0" style={{ background: hex }} />
                    <div>
                      <span className="font-readout text-[10px] text-rack-bone/28">
                        {String(i + 1).padStart(2, '0')}
                      </span>
                      <h3 className="mt-1 text-[15px] font-medium leading-snug text-rack-bone/92">
                        {d.name}
                      </h3>
                    </div>
                  </div>

                  <dl className="space-y-3.5">
                    {(
                      [
                        ['问题', d.pain, 'text-rack-bone/50'],
                        ['交付', d.deliver, 'text-rack-bone/72'],
                        ['验收', d.accept, 'text-rack-bone/72'],
                      ] as const
                    ).map(([k, v, tone]) => (
                      <div key={k} className="grid gap-x-5 gap-y-1 sm:grid-cols-[52px_1fr]">
                        <dt className="pt-px font-readout text-[9.5px] uppercase tracking-[0.16em] text-rack-bone/26">
                          {k}
                        </dt>
                        <dd className={`max-w-[64ch] text-[13px] leading-[1.95] ${tone}`}>{v}</dd>
                      </div>
                    ))}
                  </dl>
                </article>
              ))}
            </div>
          </Block>

          {/* ── 做过什么 ── */}
          <Block
            no="03"
            title="做过什么"
            hex={hex}
            note="状态照实标；只有实测能打开的才挂链接，不挂死链充数。"
          >
            <div className="border-t border-rack-bone/12">
              {set.works.map((w, i) => (
                <article
                  key={w.name}
                  className="grid gap-x-7 gap-y-3 border-b border-rack-bone/[0.07] py-7 md:grid-cols-[44px_1fr_176px]"
                >
                  <div className="flex items-start gap-2.5">
                    <span className="mt-[7px] h-px w-2.5 shrink-0" style={{ background: hex }} />
                    <span className="font-readout text-[11px] text-rack-bone/30">
                      {String(i + 1).padStart(2, '0')}
                    </span>
                  </div>

                  <div>
                    <h3 className="flex flex-wrap items-baseline gap-x-3 gap-y-1 text-[15px] font-medium leading-snug text-rack-bone/92">
                      {w.name}
                      <StatusTag status={w.status} hex={hex} />
                    </h3>
                    <p className="mt-2.5 max-w-[58ch] text-[13px] leading-[1.95] text-rack-bone/48">
                      {w.what}
                    </p>
                    {w.url && (
                      <a
                        href={w.url}
                        target="_blank"
                        rel="noreferrer noopener"
                        className="mt-3 inline-block border-b border-rack-bone/20 pb-0.5 font-readout text-[11px] tracking-[0.04em] text-rack-bone/62 transition-colors hover:border-rack-amber hover:text-rack-amber"
                      >
                        {w.url.replace(/^https?:\/\//, '').replace(/\/$/, '')} ↗
                      </a>
                    )}
                  </div>

                  <ul className="flex flex-wrap gap-x-3 gap-y-1.5 md:flex-col md:gap-y-2">
                    <li className="font-readout text-[10px] uppercase tracking-[0.1em] text-rack-bone/38">
                      {w.platform}
                    </li>
                    {w.tech.map((t) => (
                      <li
                        key={t}
                        className="font-readout text-[10px] uppercase tracking-[0.1em] text-rack-bone/26"
                      >
                        {t}
                      </li>
                    ))}
                  </ul>
                </article>
              ))}
            </div>
          </Block>

          <p className="mt-7 max-w-[68ch] text-[11.5px] leading-relaxed text-rack-bone/30">
            {set.boundary}
          </p>

          {/* ── 收口：往回、往下一个通道、或者直接联系 ── */}
          <div className="mt-16 flex flex-col gap-6 border-t border-rack-bone/12 py-12 sm:flex-row sm:items-end sm:justify-between">
            <div>
              <p className="font-songti text-[clamp(1.1rem,2.6vw,1.45rem)] font-semibold leading-[1.5] tracking-[0.08em] text-rack-bone/92">
                你的场景不在上面？
              </p>
              <p className="mt-3 max-w-[46ch] text-[13px] leading-[1.9] text-rack-bone/48">
                多数项目都不完全对得上现成的条目。把你的情况说清楚，我先判断能不能做、
                值不值得做——判断不收钱。
              </p>
              <Link
                to="/#contact"
                className="mt-6 inline-flex items-baseline gap-3 border-b border-rack-amber/30 pb-1.5 transition-colors hover:border-rack-amber"
              >
                <span className="font-readout text-[9.5px] uppercase tracking-[0.18em] text-rack-bone/28">
                  联系
                </span>
                <span className="font-readout text-[15px] tracking-[0.06em] text-rack-amber">
                  说说你的场景 →
                </span>
              </Link>
            </div>

            <Link
              to={`/cases/${next.id}`}
              className="group shrink-0 text-right transition-colors"
              aria-label={`下一个通道：${next.name}案例`}
            >
              <span className="font-readout text-[9.5px] uppercase tracking-[0.18em] text-rack-bone/26">
                NEXT · {next.no}
              </span>
              <span className="mt-1.5 block font-songti text-[17px] font-semibold tracking-[0.12em] text-rack-bone/70 transition-colors group-hover:text-rack-bone">
                {next.name}案例 →
              </span>
            </Link>
          </div>
        </div>
      </main>

      <RackFooter />
    </div>
  );
}

/** 段落抬头：编号 + 标题 + 一句说明，三节共用 */
function Block({
  no,
  title,
  note,
  hex,
  children,
}: {
  no: string;
  title: string;
  note: string;
  hex: string;
  children: React.ReactNode;
}) {
  return (
    <section className="mt-16">
      <div className="flex flex-wrap items-baseline gap-x-4 gap-y-1.5">
        <span className="font-readout text-[10px] tracking-[0.2em]" style={{ color: hex }}>
          {no}
        </span>
        <h2 className="font-songti text-[clamp(1.15rem,2.6vw,1.5rem)] font-semibold tracking-[0.12em] text-rack-bone">
          {title}
        </h2>
      </div>
      <p className="mt-3 max-w-[58ch] text-[12.5px] leading-[1.9] text-rack-bone/40">{note}</p>
      <div className="mt-7">{children}</div>
    </section>
  );
}

/** 状态标签。「线上可访问」才给通道色，其余一律灰——不让在建的项目看起来像已交付 */
function StatusTag({ status, hex }: { status: Status; hex: string }) {
  const live = status === 'live';
  return (
    <span
      className="rounded-sm border px-2 py-[3px] font-readout text-[9.5px] uppercase tracking-[0.12em]"
      style={
        live
          ? { borderColor: `${hex}55`, color: hex }
          : { borderColor: 'rgba(230,224,212,.14)', color: 'rgba(230,224,212,.4)' }
      }
    >
      {STATUS_LABEL[status]}
    </span>
  );
}
